import Slide from "@mui/material/Slide";
import { useDeepCompareMemo } from "@react-hookz/web";
import { useSetAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import type { Props as AdSlotProps } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { DynamicAdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot/dynamics";
import { isShowTakeoverAdSlotAtom } from "scmp-app/components/advertisement/takeover-ad-slot/atoms";
import { useTakeoverBottomAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot/hooks";
import type { TypeMagazinesStyleVariant } from "scmp-app/components/section/section-style/const";
import {
  useScrollOverThreshold,
  useWindowBottomReached,
} from "scmp-app/lib/hooks/use-scroll-over-threshold";

import CloseIcon from "./icon-close.svg";
import { AdSlotContainer, CloseButton, Container } from "./styles";

type Props = {
  adSlotProps?: Pick<AdSlotProps, "targeting" | "zone">;
  className?: string;
  variant?: "home" | TypeMagazinesStyleVariant;
};

export const TakeoverAdSlot: FunctionComponent<Props> = ({ adSlotProps, className, variant }) => {
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const isTablet = useResponsive(theme.breakpoints.only("tablet"));
  const { isEnableTakeoverBottomAdSlot, takeoverAdSlotValue } = useTakeoverBottomAdSlot({
    adSlotProps,
    variant,
  });
  const [isClose, setIsClose] = useState(false);

  // SCMPWEB-10011 T2-B: sentinel-driven visibility, replacing the ahooks
  // useScroll listener + 200ms throttled continuous evaluation (per-frame JS
  // with layout reads). Parity with the old steady-state machine:
  //   past gate (desktop 300 / mobile-tablet 200) && not at page bottom → show
  //   otherwise (also: above the gate, or at the page bottom) → hide
  // The bottom sentinel is repositioned by ResizeObservers on document /
  // viewport resizes, so "at bottom" stays correct as content height changes.
  const passedGate = useScrollOverThreshold(isDesktopUp ? 300 : 200, "Takeover show gate");
  const reachedBottom = useWindowBottomReached("Takeover hide at page bottom");
  const isAdVisible = passedGate && !reachedBottom;
  const isShow = isEnableTakeoverBottomAdSlot && isAdVisible;
  const setIsShowTakeoverAdSlot = useSetAtom(isShowTakeoverAdSlotAtom);

  useEffect(() => {
    setIsShowTakeoverAdSlot(isShow);

    return () => {
      setIsShowTakeoverAdSlot(false);
    };
  }, [isShow, setIsShowTakeoverAdSlot]);

  const responsiveAdSlot = useDeepCompareMemo(() => {
    if (!takeoverAdSlotValue) return null;

    return isDesktopUp ? (
      <DynamicAdSlot
        {...takeoverAdSlotValue}
        sizes={[
          [970, 90],
          [728, 90],
        ]}
      />
    ) : (
      <DynamicAdSlot
        {...takeoverAdSlotValue}
        sizes={[
          [300, 50],
          [320, 50],
        ]}
      />
    );
  }, [takeoverAdSlotValue, isDesktopUp]);

  if (!isEnableTakeoverBottomAdSlot || isClose || isTablet) return null;

  return (
    <Slide className={className} direction="up" in={isAdVisible} timeout={300}>
      <Container>
        <CloseButton onClick={() => setIsClose(true)}>
          <CloseIcon />
        </CloseButton>
        <AdSlotContainer $height={isDesktopUp ? 90 : 50}>{responsiveAdSlot}</AdSlotContainer>
      </Container>
    </Slide>
  );
};

TakeoverAdSlot.displayName = "TakeoverAdSlot";
